import React from 'react';
import { ACCIONES, COLUMNAS_MATRIZ, comparacionTerminada, filasMatriz } from './comparacion';
import type { ComparacionIA } from './tipos';
import { Aviso, LABEL_ACCION } from './ui';

const LABEL_COLUMNA: Record<(typeof COLUMNAS_MATRIZ)[number], string> = { ...LABEL_ACCION, fallida: 'FALLÓ' };

/**
 * Lo que devolvió la comparación: el resumen de la IA y sus tendencias por corrida, y la
 * matriz humano × modelo por corrida (métricas del backend, que existen aunque la IA
 * haya fallado).
 */
const ResultadoComparacionIA: React.FC<{ comparacion: ComparacionIA; etiquetaDe: (runId: string) => string }> = ({ comparacion, etiquetaDe }) => {
  if (!comparacionTerminada(comparacion)) return null;
  const res = comparacion.resultado ?? null;
  const ia = res?.ia ?? null;
  return (
    <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
      <Aviso error={comparacion.error ? `La comparación terminó con error: ${comparacion.error}` : null} />
      {!res && <p className="text-sm text-gray-500">La comparación no dejó resultado.</p>}

      {ia ? (
        <>
          <div>
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Resumen de la IA</h4>
            <p className="whitespace-pre-wrap text-sm text-brand-black">{ia.resumen}</p>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {ia.por_corrida.map((c) => (
              <div key={c.run_id} className="rounded-lg bg-gray-50 p-3 text-xs">
                <div className="mb-1 font-semibold text-brand-black">{etiquetaDe(c.run_id)}</div>
                <Lista titulo="Tendencias" items={c.tendencias} color="text-gray-700" />
                <Lista titulo="Fortalezas" items={c.fortalezas} color="text-green-700" />
                <Lista titulo="Debilidades" items={c.debilidades} color="text-red-700" />
              </div>
            ))}
          </div>
        </>
      ) : (
        res && <p className="text-sm text-gray-500">La lectura con IA no está disponible (falló); quedan las métricas.</p>
      )}

      {res && res.metricas.length > 0 && (
        <div>
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Tu acción × acción del modelo</h4>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {res.metricas.map((m) => (
              <div key={m.run_id} className="rounded-lg bg-gray-50 p-3 text-xs">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <b className="text-brand-black">{etiquetaDe(m.run_id)}</b>
                  <span className="text-gray-500">
                    coincide {m.coincide_accion} / {m.analizadas}
                    {m.pct_coincide !== null && ` (${Math.round(m.pct_coincide)}%)`} · {m.con_referencia} con referencia
                  </span>
                </div>
                <table className="w-full tabular-nums">
                  <thead>
                    <tr className="text-[10px] text-gray-500">
                      <th className="py-0.5 pr-2 text-left font-medium">yo ↓ / modelo →</th>
                      {COLUMNAS_MATRIZ.map((c) => (
                        <th key={c} className="py-0.5 px-1 text-center font-medium">
                          {LABEL_COLUMNA[c]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filasMatriz(m.matriz).map((f) => (
                      <tr key={f.humano} className="border-t border-gray-200">
                        <td className="py-0.5 pr-2 text-left text-gray-700">
                          {LABEL_ACCION[f.humano]} <span className="text-gray-400">({f.total})</span>
                        </td>
                        {f.celdas.map((c) => (
                          <td key={c.modelo} className={`py-0.5 px-1 text-center ${c.acierto ? 'bg-green-100 font-semibold text-green-800' : c.n > 0 ? 'text-red-700' : 'text-gray-400'}`}>
                            {c.n}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
          <p className="mt-1 text-[10px] text-gray-400">Filas: {ACCIONES.map((a) => LABEL_ACCION[a]).join(' / ')} según tu análisis. Diagonal en verde = misma acción.</p>
        </div>
      )}
    </div>
  );
};

const Lista: React.FC<{ titulo: string; items: string[]; color: string }> = ({ titulo, items, color }) =>
  items.length === 0 ? null : (
    <div className="mb-1">
      <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">{titulo}</div>
      <ul className={`list-disc pl-4 ${color}`}>
        {items.map((t, i) => (
          <li key={`${t}-${String(i)}`}>{t}</li>
        ))}
      </ul>
    </div>
  );

export default ResultadoComparacionIA;
